import { MaterialIcons } from '@expo/vector-icons';
import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';

import PrimaryButton from '../../shared/ui/PrimaryButton';
import { uiText } from '../../shared/utils/uiText';

interface Invitation {
  id: string;
  families?: { name?: string };
  invited_by?: { name?: string } | string;
  user_profiles?: { name?: string } | string;
  invite_token?: string;
}

interface InvitationCardProps {
  invite: Invitation;
  onAccept: (invite: Invitation) => void;
  onReject: (invite: Invitation) => void;
  onShare: (invite: Invitation) => void;
  shareLoading: boolean;
  styles: any;
}

const InvitationCard: React.FC<InvitationCardProps> = ({ invite, onAccept, onReject, onShare, shareLoading, styles }) => (
  <View style={premiumStyles.worldClassCard}>
    {/* Premium Header with Icon */}
    <View style={premiumStyles.cardHeader}>
      <View style={premiumStyles.iconContainer}>
        <MaterialIcons name="mail" size={24} color="#4F46E5" />
      </View>
      <View style={premiumStyles.headerContent}>
        <Text style={premiumStyles.familyName}>{invite.families?.name || 'Unknown Family'}</Text>
        <Text style={premiumStyles.inviterName}>
          Invited by {typeof invite.invited_by === 'string' ? invite.invited_by : invite.invited_by?.name || (typeof invite.user_profiles === 'string' ? invite.user_profiles : invite.user_profiles?.name) || 'Unknown'}
        </Text>
      </View>
    </View>

    {/* Premium Action Buttons */}
    <View style={premiumStyles.actionRow}>
      <View style={premiumStyles.primaryActions}>
        <PrimaryButton
          title={uiText('accept')}
          onPress={() => onAccept(invite)}
          style={premiumStyles.acceptButton}
        />
        <PrimaryButton
          title={uiText('decline')}
          onPress={() => onReject(invite)}
          style={premiumStyles.declineButton}
        />
      </View>
      {invite.invite_token && (
        <PrimaryButton
          title={shareLoading ? 'Sharing...' : 'Share'}
          onPress={() => onShare(invite)}
          style={premiumStyles.shareButton}
          disabled={shareLoading}
        />
      )}
    </View>
  </View>
);

// Digital Sanctuary Design System Styles
const premiumStyles = StyleSheet.create({
  worldClassCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    ...Platform.select({
      ios: {
        shadowColor: '#1D1A23',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0px 4px 16px rgba(29, 26, 35, 0.08)',
      },
    }),
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    ...Platform.select({
      ios: {
        shadowColor: '#4F46E5',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0px 2px 8px rgba(79, 70, 229, 0.15)',
      },
    }),
  },
  headerContent: {
    flex: 1,
  },
  familyName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1D1A23',
    marginBottom: 4,
    letterSpacing: -0.3,
  },
  inviterName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#777587',
    lineHeight: 18,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  primaryActions: {
    flexDirection: 'row',
    flex: 1,
  },
  acceptButton: {
    flex: 1,
    marginRight: 8,
  },
  declineButton: {
    flex: 1,
    marginRight: 12,
  },
  shareButton: {
    minWidth: 80,
  },
});

export default InvitationCard;
